import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiArrowLeft, FiRefreshCw, FiChevronRight } from 'react-icons/fi';
import './OtpAuthPage.css';
import vedonicaLogo from '../assets/images/vedonica_logo.jpeg';

/**
 * RegisterPage — phone + OTP only.
 * No name, no email. Account banata hai automatically.
 * Same logic as LoginPage — agar user exist karta hai toh login ho jaata hai.
 */
const RegisterPage = () => {
  const { loginWithOtp, registerWithOtp, requestOtp } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer(r => r - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) { toast.error('Enter a valid 10-digit number'); return; }
    setLoading(true);
    try {
      await requestOtp({ phone: '+91' + phone });
      setStep('otp');
      setResendTimer(30);
      toast.success('OTP sent!');
      setTimeout(() => otpRefs.current[0]?.focus(), 150);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp]; next[i] = val.slice(-1); setOtp(next);
    if (val && i < 5) otpRefs.current[i + 1]?.focus();
  };
  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };
  const handleOtpPaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) { setOtp(text.split('')); otpRefs.current[5]?.focus(); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    setLoading(true);
    try {
      // Try login first (existing user)
      const user = await loginWithOtp({ phone: '+91' + phone, otp: code });
      toast.success(`Welcome back! 👋`);
      navigate('/');
    } catch (err) {
      const isNewUser = err.response?.status === 404 || err.response?.data?.is_new_user;
      if (isNewUser) {
        // New user — register with phone only, no name/email needed
        try {
          await registerWithOtp({
            phone: '+91' + phone,
            otp:   code,
            // first_name, last_name, email — all optional, not sent
          });
          toast.success('Welcome to Vedonica International! ');
          navigate('/');
        } catch (regErr) {
          const errors = regErr.response?.data;
          if (errors && typeof errors === 'object') {
            Object.values(errors).flat().forEach(m => toast.error(m));
          } else {
            toast.error('Something went wrong. Please try again.');
          }
          setOtp(['', '', '', '', '', '']);
          otpRefs.current[0]?.focus();
        }
      } else {
        toast.error(err.response?.data?.error || 'Invalid OTP');
        setOtp(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      await requestOtp({ phone: '+91' + phone });
      setResendTimer(30);
      setOtp(['', '', '', '', '', '']);
      toast.success('OTP resent!');
      setTimeout(() => otpRefs.current[0]?.focus(), 150);
    } catch { toast.error('Failed to resend OTP'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Helmet><title>Get Started – Vedonica International</title></Helmet>
      <div className="otp-page">
        <div className="otp-card">
          <Link to="/" className="otp-card__brand">
  <img src={vedonicaLogo} alt="Vedonica" className="otp-card__logo-img" />
  
</Link>

          {step === 'phone' ? (
            <>
              <h1 className="otp-card__title">Welcome to<br />Ayurvedic World</h1>
              <p className="otp-card__subtitle">Enter your mobile number to get started</p>

              <form onSubmit={handleSendOtp} className="otp-form">
                <div className="otp-phone-input-wrap">
                  <div className="otp-phone-prefix">
                    <span className="otp-flag">🇮🇳</span>
                    <span>+91</span>
                  </div>
                  <input
                    type="tel"
                    className="otp-phone-input"
                    placeholder="10-digit mobile number"
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    maxLength={10}
                    required
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary otp-submit"
                  disabled={loading || phone.length !== 10}
                >
                  {loading
                    ? <span className="otp-btn-loading"><span className="spinner-sm" /> Sending...</span>
                    : <><span>Get OTP</span> <FiChevronRight size={16} /></>}
                </button>
              </form>

              <p className="otp-card__note">
                Already have an account? <Link to="/login">Login here</Link>
              </p>
            </>
          ) : (
            <>
              <button className="otp-back-btn" onClick={() => { setStep('phone'); setOtp(['','','','','','']); }}>
                <FiArrowLeft size={14} /> Edit Number
              </button>

              <div className="otp-card__icon">🔐</div>
              <h1 className="otp-card__title">Enter OTP</h1>
              <p className="otp-card__subtitle">Sent to <strong>+91 {phone}</strong></p>

              <form onSubmit={handleVerify} className="otp-form">
                <div className="otp-boxes" onPaste={handleOtpPaste}>
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => otpRefs.current[i] = el}
                      type="text" inputMode="numeric" maxLength={1}
                      className={`otp-box ${digit ? 'otp-box--filled' : ''}`}
                      value={digit}
                      onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                    />
                  ))}
                </div>
                <button
                  type="submit"
                  className="btn btn-primary otp-submit"
                  disabled={loading || otp.join('').length !== 6}
                >
                  {loading
                    ? <span className="otp-btn-loading"><span className="spinner-sm" /> Verifying...</span>
                    : 'Verify & Continue'}
                </button>
              </form>

              <div className="otp-resend">
                {resendTimer > 0
                  ? <p className="otp-resend__timer">Resend in <strong>{resendTimer}s</strong></p>
                  : <button className="otp-resend__btn" onClick={handleResend} disabled={loading}>
                      <FiRefreshCw size={13} /> Resend OTP
                    </button>
                }
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default RegisterPage;